/** @fileoverview Builds the calls that read checklist and timeline rows, run actions, tick rows, and read a screen's charts, for the builder or a hosted app. */

import type {
  AppActionRunResult,
  ComponentStateRead,
  ComponentStateRow,
  FabrikChartsRead,
  FabrikGlobals,
  FabrikListFilterChoices,
  FabrikRecordKeyRefValue,
  InputSnapshot,
  FabrikRowValues,
} from '../types/apps';
import { SERVER_SET_GLOBAL_FIELDS } from '../components/AppRenderer/globals';
import type { RuntimeFile } from './runtimeResponse';

/** Row values keyed by datasource id; state requests send only their configured key fields. */
export type StateFields = FabrikRowValues;

/** Key under which the globals values of the record keys are sent to the server in `fields`. */
const GLOBALS_FIELDS_KEY = 'GLOBALS';

/** One JSON request to a runtime route, plus the messages to show when it fails. */
export type RuntimeRequest = {
  path: string;
  method: 'POST' | 'PATCH';
  body: unknown;
  signal?: AbortSignal;
  /** Used by senders that turn failures into plain messages; others may ignore them. */
  messages: { notFound: string; failed: string };
};

/** Sends one request and returns the parsed JSON answer, nothing for 204, or a `RuntimeFile` for a file answer. */
export type ComponentStateSend = (request: RuntimeRequest) => Promise<unknown>;

/** Reads, writes, and ticks checklist and timeline rows for one mode. */
export type ComponentStateApi = {
  read: (request: {
    componentId: string;
    screenId: string;
    fields: StateFields;
    inputs?: InputSnapshot;
    recordKey: FabrikRecordKeyRefValue[];
    signal?: AbortSignal;
  }) => Promise<ComponentStateRead>;
  runAction: (request: {
    actionId: string;
    screenId: string;
    fields: StateFields;
    inputs?: InputSnapshot;
    /** Record keys of the components the action writes to; their globals go along. */
    recordKey: FabrikRecordKeyRefValue[];
    /** Set when the action is run from a checklist row button. */
    item?: { componentId: string; key: string };
    /** Names one press of an action that writes to a system; the server writes once per key. */
    runKey?: string;
  }) => Promise<AppActionRunResult | RuntimeFile>;
  tick: (request: {
    componentId: string;
    key: string;
    screenId: string;
    fields: StateFields;
    inputs?: InputSnapshot;
    recordKey: FabrikRecordKeyRefValue[];
    checked: boolean;
  }) => Promise<ComponentStateRow>;
  /** Reads the numbers of every chart on one screen, with the filter chips picked in its lists. */
  readCharts: (request: {
    screenId: string;
    fields: StateFields;
    inputs?: InputSnapshot;
    /** Refs the charted lists' row filters and effective dates read; their globals go along. */
    recordKey: FabrikRecordKeyRefValue[];
    listFilters: FabrikListFilterChoices;
    signal?: AbortSignal;
  }) => Promise<FabrikChartsRead>;
  /** Asks the server for the address of a link with blanks, for the row or record it was clicked on. */
  resolveLink: (request: {
    screenId: string;
    componentId: string;
    fields: StateFields;
  }) => Promise<{ url: string }>;
};

const DATA_ERROR = 'Unable to load the data. Please try again.';

/**
 * Adds, under `GLOBALS`, the globals values the record keys use, so the server builds the record
 * id from the same clock the renderer showed. Returns the fields as they are when no key uses globals.
 * @param fields row values to send
 * @param recordKey record keys of the components the request reads or writes
 * @param globals the globals values of this render
 */
const withRecordKeyGlobals = (
  fields: StateFields,
  recordKey: FabrikRecordKeyRefValue[],
  globals: FabrikGlobals
): StateFields => {
  const keyGlobals: Record<string, unknown> = {};
  for (const ref of recordKey) {
    if (
      ref.scope === 'globals' &&
      !SERVER_SET_GLOBAL_FIELDS.has(ref.field) &&
      Object.prototype.hasOwnProperty.call(globals, ref.field)
    ) {
      keyGlobals[ref.field] = globals[ref.field];
    }
  }
  return Object.keys(keyGlobals).length ? { ...fields, [GLOBALS_FIELDS_KEY]: keyGlobals } : fields;
};

/**
 * Builds the component state calls on top of one base path and one sender.
 * @param options base path of the routes, the function that sends a request, and the globals
 *   values the record keys read
 */
export const makeComponentStateApi = ({
  basePath,
  send,
  globals,
}: {
  basePath: string;
  send: ComponentStateSend;
  globals: FabrikGlobals;
}): ComponentStateApi => ({
  read: async ({ componentId, screenId, fields, inputs, recordKey, signal }) =>
    (await send({
      path: `${basePath}/state/read`,
      method: 'POST',
      body: {
        componentId,
        screenId,
        fields: withRecordKeyGlobals(fields, recordKey, globals),
        ...(inputs ? { inputs } : {}),
      },
      signal,
      messages: { notFound: "This block couldn't be found.", failed: DATA_ERROR },
    })) as ComponentStateRead,
  runAction: async ({ actionId, screenId, fields, inputs, recordKey, item, runKey }) =>
    (await send({
      path: `${basePath}/actions/${encodeURIComponent(actionId)}/run`,
      method: 'POST',
      body: {
        screenId,
        fields: withRecordKeyGlobals(fields, recordKey, globals),
        ...(inputs ? { inputs } : {}),
        item,
        ...(runKey ? { runKey } : {}),
      },
      messages: {
        notFound: "This action couldn't be found.",
        failed: 'Unable to run this action. Please try again.',
      },
    })) as AppActionRunResult | RuntimeFile,
  tick: async ({ componentId, key, screenId, fields, inputs, recordKey, checked }) =>
    (await send({
      path: `${basePath}/state/${encodeURIComponent(componentId)}/${encodeURIComponent(key)}`,
      method: 'PATCH',
      body: {
        screenId,
        fields: withRecordKeyGlobals(fields, recordKey, globals),
        ...(inputs ? { inputs } : {}),
        value: { checked },
      },
      messages: {
        notFound: "This checklist item couldn't be found.",
        failed: 'Unable to save this change. Please try again.',
      },
    })) as ComponentStateRow,
  readCharts: async ({ screenId, fields, inputs, recordKey, listFilters, signal }) =>
    (await send({
      path: `${basePath}/charts/read`,
      method: 'POST',
      body: {
        screenId,
        fields: withRecordKeyGlobals(fields, recordKey, globals),
        ...(inputs ? { inputs } : {}),
        listFilters,
      },
      signal,
      messages: {
        notFound: "This screen's charts couldn't be found.",
        failed: 'Unable to load the charts. Please try again.',
      },
    })) as FabrikChartsRead,
  resolveLink: async ({ screenId, componentId, fields }) =>
    (await send({
      path: `${basePath}/links`,
      method: 'POST',
      body: { screenId, componentId, fields },
      messages: {
        notFound: 'This link no longer exists. Reload the app.',
        failed: "This link couldn't be opened. Please try again.",
      },
    })) as { url: string },
});
